import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  Inject,
  AfterViewInit,
} from "@angular/core";
import {
  Utility,
  BeadTypeEnum,
  BeadMeasureEnum,
  getBeadTypeForPalette,
  getBuiltInPaletteId,
  getBuiltInPaletteName,
} from "../helpers/utility";
import { PaletteRegistryService } from "../services/palette-registry.service";
import {
  BeadCanvas,
  ShiftTypeEnum,
  FlipDirectionEnum,
  EditMode,
} from "../peditor/beadcanvas";
import { PatternPalettesListComponent } from "../pattern-palettes-list/pattern-palettes-list.component";
import { HttpService, UnitOfMeasureEnum } from "../services/http-service";
import { PaletteDetailModel } from "../models/palettes/palette_detail_model";
import { StitchModel, StitchTypeEnum } from "../models/stitchs/stitch_model";
import {
  PatternExplorerModalMode,
  PatternExplorerModal,
} from "../modals/pattern-explorer-modal/pattern-explorer-modal";
import { MatDialog } from "@angular/material/dialog";
import {
  AddEditPatternModal,
  AddEditPatternModalMode,
} from "../modals/add-edit-pattern-modal/add-edit-pattern-modal";
import {
  ToolbarActionFiredTypeEnum,
  ToolbarActionFired,
  ToolbarComponent,
} from "../toolbar/toolbar.component";
import { PrintPreviewModal } from "../modals/print-preview-modal/print-preview-modal";
import { PatternReaderModal } from "../modals/pattern-reader-modal/pattern-reader-modal";
import { PaletteEditorModal } from "../modals/palette-editor-modal/palette-editor-modal";
import {
  TransferImageModalResult,
  TransferImageModal,
} from "../modals/transfer-image-modal/transfer-image-modal";
import { Observable, timer, Subscription } from "rxjs";
import { RotateDirectionEnum, Action } from "../peditor/patternmodelcontroller";
import { GoogleAuthService, GoogleUser } from "../services/google-auth.service";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { TransformPatternModal } from "../modals/transform-pattern-modal/transform-pattern-modal";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { SubscriptionsModal } from "../modals/subscriptions-modal/subscriptions-modal";
import { SubscriptionTypeEnum } from "../models/user/user-model";
import { ChangePlanDoneModal } from "../modals/change-plan-done-modal/change-plan-done-modal";
import { PatternModel } from "../models/pattern/pattern-model";
import * as dayjs from "dayjs";
import { BeadCanvasComponent } from "../bead-canvas/bead-canvas.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NotificationTypeEnum } from "../components/notification/notification.component";
import { Rect } from "../peditor/rect";
import { Title } from "@angular/platform-browser";
import { PalettesListComponent } from "../palettes-list/palettes-list.component";
import { LocalStorageKeys, SUBSCRIPTIONS } from "../helpers/constants";
import { DOCUMENT } from "@angular/common";
import {
  OnboardingService,
  OnboardingPath,
} from "../services/onboarding.service";
import { DeviceDetectorService } from "ngx-device-detector";
import { PosthogService } from "../services/posthog.service";
import { WelcomeOnboardingModalComponent } from "../modals/welcome-onboarding-modal/welcome-onboarding-modal.component";
import {
  VideoPlayerModalComponent,
  VideoWatchStats,
} from "../modals/video-player-modal/video-player-modal.component";
import { PositionImageGuideModal } from "../modals/position-image-guide-modal/position-image-guide-modal";
import { OnboardingChecklistComponent } from "../components/onboarding-checklist/onboarding-checklist.component";
import {
  NewPatternSetupModalComponent,
  PatternSetupResult,
} from "../modals/new-pattern-setup-modal/new-pattern-setup-modal.component";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.css"],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  stitchs: StitchModel[] = [];
  selectedPaletteDetail: PaletteDetailModel;

  patternListHeight: number;
  paletteListHeight: number;

  rightSidebarOpen: boolean = false; // Start closed, will be opened in ngOnInit if desktop

  windowWidth: number;
  windowHeight: number;

  minSideToolbarWidth = 510;

  currentPattern: PatternModel;

  pleaseWait: boolean = false;

  private hasProcessedAuthState: boolean = false; // Prevent duplicate login processing
  private isLoggingOut: boolean = false; // Prevent redirect loops during logout
  private paletteMismatchSubscription: Subscription | null = null; // Palette mismatch error subscription

  // Help panel properties
  helpPanelOpen: boolean = false;
  supportEmailLink: string =
    "mailto:support@beadpatternmaker.com?subject=Bead%20Pattern%20Maker%20Help";
  emailCopied: boolean = false;
  helpFaqItems: Array<{ questionKey: string; answerKey: string }> = [
    { questionKey: "HelpFaqUploadQuestion", answerKey: "HelpFaqUploadAnswer" },
    { questionKey: "HelpFaqSaveQuestion", answerKey: "HelpFaqSaveAnswer" },
    { questionKey: "HelpFaqExportQuestion", answerKey: "HelpFaqExportAnswer" },
  ];
  private dialogOpenCount: number = 0;

  // Save reminder tracking
  private beadPlacementCount: number = 0;
  private lastSaveReminderTime: number = Date.now(); // Initialize to current time
  private hasShownSaveHint: boolean = false;
  showSaveReminder: boolean = false;

  // Mobile save button - only shown on mobile when user has made changes
  showMobileSaveButton: boolean = false;
  showMobileSavePulse: boolean = false;

  // Early warning banner (2/3 patterns used)
  showEarlyWarningBanner: boolean = false;
  private earlyWarningDismissed: boolean = false;

  // Session engagement tracking
  private sessionMetrics = {
    sessionStart: Date.now(),
    toolsUsed: new Set<string>(),
    colorsUsed: new Set<string>(),
    beadsPlaced: 0,
    beadsRemoved: 0,
    undoCount: 0,
    redoCount: 0,
    zoomCount: 0,
    fillsUsed: 0,
    lastActivityTime: Date.now(),
  };

  // Track if user has made their first meaningful edit
  private hasCreatedPattern: boolean = false;

  // Track if user has completed their first pattern save (for user_activated_first_time event)
  private hasCompletedFirstSave: boolean = false;

  // Performance tracking
  private homeComponentLoadStart: number = performance.now();
  private firstInteractionTracked: boolean = false;

  // Track if we're in initial load to prevent unwanted scrolls
  private isInitialLoad: boolean = true;

  @ViewChild("patternPaletteList", { static: true })
  private patternPaletteList: PatternPalettesListComponent;

  @ViewChild("paletteList", { static: true })
  private paletteList: PalettesListComponent;

  @ViewChild("beadCanvasComponent", { static: true })
  private beadCanvasComponent: BeadCanvasComponent;

  @ViewChild("appToolbar", { static: true })
  private appToolbar: ToolbarComponent;

  @ViewChild(OnboardingChecklistComponent)
  private checklistComponent: OnboardingChecklistComponent;

  appToolbarHeight: number;

  smallToolbarLeft: number;
  smallToolbarTop: number;

  bcanvas: BeadCanvas;

  lastTemporaryPatternData: string;

  lastActionId: number = 0;
  checkAutoSave: Observable<number> = timer(0, 1000);
  checkAutoSaveSubscription: Subscription;

  selectedEditMode: string = "add-bead";
  currentBeadColor: string | null = null;
  selectedTabIndex: number = 0;

  hasTouchScreen: boolean;

  // Keyboard shortcuts for floating toolbar (only shown on desktop)
  brushShortcut: string = "";
  eraserShortcut: string = "";
  fillShortcut: string = "";
  removeFillShortcut: string = "";
  pickBeadShortcut: string = "";
  panShortcut: string = "";

  constructor(
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    public httpService: HttpService,
    private googleAuthService: GoogleAuthService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private onboardingService: OnboardingService,
    private deviceService: DeviceDetectorService,
    private posthog: PosthogService,
    private paletteRegistry: PaletteRegistryService,
  ) {
    console.log(
      "[SaveReminder] HomeComponent initialized - Save reminder system ready",
    );

    // Initialize BeadCanvas with palette registry
    this.bcanvas = new BeadCanvas();
    this.bcanvas.setPaletteRegistry(this.paletteRegistry);

    this.hasTouchScreen = Utility.hasTouchScreen();

    // Initialize keyboard shortcuts for desktop users
    if (this.deviceService.isDesktop()) {
      this.brushShortcut = "— B";
      this.eraserShortcut = "— E";
      this.fillShortcut = "— F";
      this.removeFillShortcut = "— R";
      this.pickBeadShortcut = "— I";
      this.panShortcut = "— Space";
    }
    this.stitchs.push(<StitchModel>{
      stitchId: StitchTypeEnum.Peyote,
      name: "Peyote",
    });

    this.stitchs.push(<StitchModel>{
      stitchId: StitchTypeEnum.Brick,
      name: "Brick",
    });
    this.stitchs.push(<StitchModel>{
      stitchId: StitchTypeEnum.Loom,
      name: "Loom",
    });

    this.restartAutoSaveTimer();

    if (!this.httpService || !this.httpService.loggedIn) {
      let queryParams = undefined;

      if (
        router.routerState.snapshot.url.includes("session_id") &&
        router.routerState.snapshot.url.includes("price_id")
      ) {
        queryParams = { returnUrl: router.routerState.snapshot.url };
      }

      this.router.navigate(["login"], {
        queryParams,
      } as NavigationExtras);
    }
  }

  setSidebarTabIndex(tabIndex: number) {
    this.selectedTabIndex = tabIndex;
  }

  openCloseRightSidebar(open: boolean) {
    this.rightSidebarOpen = open;
    this.beadCanvasComponent.resizeCanvasContainer();
  }

  toggleRightSidebar() {
    this.rightSidebarOpen = !this.rightSidebarOpen;
    this.beadCanvasComponent.resizeCanvasContainer();
  }

  onCanvasClick() {
    if (this.deviceService.isMobile() && this.rightSidebarOpen) {
      this.openCloseRightSidebar(false);
    }
  }

  private restartAutoSaveTimer() {
    if (this.checkAutoSaveSubscription)
      this.checkAutoSaveSubscription.unsubscribe();

    this.checkAutoSaveSubscription = this.checkAutoSave.subscribe(async () => {
      if (this.httpService && this.httpService.canSavePattern()) {
        if (this.lastActionId == this.bcanvas.getLastId()) {
          try {
            await this.savePatternWithDialog(false, true);
          } catch (error) {
            // Silently ignore auto-save errors to prevent uncaught promise rejections
            console.error("[AutoSave] Auto-save failed:", error);
          }
        }

        this.lastActionId = this.bcanvas.getLastId();
      }
    });
  }

  onSelectedEditModeChanged(e: MatButtonToggleChange): void {
    const toolName = e.value;

    // PostHog: Track tool selection
    this.sessionMetrics.toolsUsed.add(toolName);
    this.sessionMetrics.lastActivityTime = Date.now();

    this.posthog.captureEvent("tool_selected", {
      tool_name: toolName,
      session_duration_seconds: Math.round(
        (Date.now() - this.sessionMetrics.sessionStart) / 1000,
      ),
      tools_used_so_far: this.sessionMetrics.toolsUsed.size,
      beads_placed_so_far: this.sessionMetrics.beadsPlaced,
    });

    switch (e.value) {
      case "add-bead":
        this.bcanvas.setEditMode(EditMode.AddBead);
        break;
      case "fill":
        this.bcanvas.setEditMode(EditMode.Fill);
        break;
      case "remove-bead":
        this.bcanvas.setEditMode(EditMode.RemoveBead);
        break;
      case "remove-fill":
        this.bcanvas.setEditMode(EditMode.RemoveFill);
        break;
      case "pick-bead":
        this.bcanvas.setEditMode(EditMode.PickBead);
        break;
      case "pan":
        this.bcanvas.setEditMode(EditMode.Pan);
        break;
    }
  }

  public getStitch(): StitchModel {
    for (var i = 0; i < this.stitchs.length; i++)
      if (this.stitchs[i].stitchId == this.bcanvas.getStitchId())
        return this.stitchs[i];

    return this.stitchs[0];
  }

  getPhotoUrl() {
    if (
      this.httpService.socialUser &&
      this.httpService.socialUser.photoUrl &&
      this.httpService.socialUser.photoUrl != ""
    )
      return this.httpService.socialUser.photoUrl;
    else return "https://www.w3schools.com/howto/img_avatar.png";
  }

  getFullname() {
    if (this.httpService.socialUser)
      return (
        this.httpService.socialUser.firstName +
        " " +
        this.httpService.socialUser.lastName +
        " (" +
        this.getProviderName(this.httpService.socialUser.provider) +
        ")"
      );
    else return "Anonymous";
  }

  getProviderName(provider: string) {
    if (provider === "FACEBOOK") {
      return this.httpService.getTransation().Facebook;
    } else if (provider === "GOOGLE") {
      return this.httpService.getTransation().Google;
    }
    return provider; // Return the provider string itself if unknown
  }

  async signOut() {
    console.log("[HomeComponent] signOut called");
    console.log(
      "[HomeComponent] Current login status:",
      this.httpService.loggedIn,
    );
    console.log("[HomeComponent] Current userId:", this.httpService.userId);
    console.log("[HomeComponent] User Agent:", navigator.userAgent);

    // Set logging out flag to prevent redirect loops
    this.isLoggingOut = true;

    // PostHog: Track logout with session metrics
    this.posthog.captureEvent("logout", {
      session_duration_seconds: Math.round(
        (Date.now() - this.sessionMetrics.sessionStart) / 1000,
      ),
      beads_placed: this.sessionMetrics.beadsPlaced,
      tools_used: this.sessionMetrics.toolsUsed.size,
      patterns_saved_in_session:
        this.hasCreatedPattern && this.currentPattern?.patternId > 0,
    });

    // PostHog: Reset user data
    this.posthog.reset();

    // Detect if running on Chrome Android early
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isChrome =
      /Chrome/i.test(navigator.userAgent) &&
      !/Edge|Edg/i.test(navigator.userAgent);

    if (isAndroid && isChrome) {
      // On Chrome Android, clear ALL localStorage to prevent stale state issues
      console.log(
        "[HomeComponent] Chrome Android detected - clearing ALL localStorage",
      );
      localStorage.clear();
    } else {
      // On other platforms, just remove the auth provider
      localStorage.removeItem(LocalStorageKeys.AuthProvider);
      localStorage.removeItem("gis_id_token");
      console.log("[HomeComponent] Removed auth tokens from localStorage");
    }

    if (this.httpService.loggedIn) {
      console.log(
        "[HomeComponent] User is logged in, calling signOut on auth service",
      );

      // Sign out from Google Identity Services
      try {
        this.googleAuthService.signOut();
        console.log("[HomeComponent] GoogleAuthService.signOut() completed");
      } catch (error) {
        console.log(
          "[HomeComponent] GoogleAuthService.signOut() error:",
          error,
        );
      }
    } else {
      console.log(
        "[HomeComponent] User is NOT logged in, skipping authService.signOut()",
      );
    }

    // PostHog: Reset user data (second call to ensure it happens after signout)
    this.posthog.reset();

    // Reset httpService state
    this.httpService.loggedIn = false;
    this.httpService.socialUser = null;
    this.httpService.userId = null;
    this.hasProcessedAuthState = false; // Reset so we can process new login
    console.log("[HomeComponent] Reset httpService state");

    // Unregister service workers if present (Chrome Android issue fix)
    if ("serviceWorker" in navigator) {
      console.log("[HomeComponent] Unregistering service workers");
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log("[HomeComponent] Service worker unregistered");
        }
      } catch (error) {
        console.error(
          "[HomeComponent] Error unregistering service workers:",
          error,
        );
      }
    }

    console.log("[HomeComponent] Platform detection:", { isAndroid, isChrome });

    if (isAndroid && isChrome) {
      console.log(
        "[HomeComponent] Chrome Android detected - forcing full page reload",
      );
      // Force full page reload to clear any stale state
      window.location.href = "/login?nocache=" + new Date().getTime();
    } else {
      console.log("[HomeComponent] Navigating to login page");
      this.router
        .navigateByUrl("login")
        .then(() => {
          console.log("[HomeComponent] Navigation to login completed");
          // Reset the logout flag after navigation completes
          this.isLoggingOut = false;
        })
        .catch((error) => {
          console.error("[HomeComponent] Navigation to login failed:", error);
          this.isLoggingOut = false;
        });
    }
  }

  async ngOnInit(): Promise<void> {
    const ngOnInitStart = performance.now();
    console.log("[HomeComponent] ngOnInit - Home page loaded");
    console.log(
      "[HomeComponent] Environment production:",
      environment.production,
    );

    this.titleService.setTitle("Bead Pattern Maker");

    // Track dialog open state for hiding help button
    this.dialog.afterOpened.subscribe(() => {
      this.dialogOpenCount++;
    });
    this.dialog.afterAllClosed.subscribe(() => {
      this.dialogOpenCount = 0;
    });

    this.route.queryParams.subscribe((params) => {
      if (params.session_id) {
        this.router.navigateByUrl("");
        this.showChangePlanDone();

        const subscription = SUBSCRIPTIONS.find(
          (s) => s.item_id === params.price_id,
        );

        // PostHog: Track subscription purchase
        if (subscription) {
          this.posthog.captureEvent("subscription_completed", {
            plan_name: subscription.item_name,
            price: subscription.price,
            currency: "USD",
            transaction_id: params.session_id,
            payment_provider: "stripe",
            // Revenue tracking for funnel optimization
            revenue: subscription.price,
            is_annual: subscription.item_name.toLowerCase().includes("yearly"),
          });

          // PostHog: Update user properties
          this.posthog.setUserProperties({
            subscription_status: "active",
            subscription_plan: subscription.item_name,
            subscription_start_date: new Date().toISOString(),
            lifetime_value: subscription.price,
          });
        }
      } else if (params.checkout_cancelled === "true") {
        // Track when user abandons Stripe checkout and returns
        this.posthog.captureEvent("checkout_abandoned", {
          return_reason: "user_cancelled",
          source: "stripe_redirect",
        });
      }
    });

    console.log("[HomeComponent] Subscribing to authState");

    // Set up a timeout to redirect to login if auth state never fires
    // This handles edge cases where the auth provider fails silently
    const authTimeout = setTimeout(() => {
      if (!this.hasProcessedAuthState && !this.httpService.loggedIn) {
        console.warn(
          "[HomeComponent] Auth state timeout - no auth response after 10 seconds",
        );

        // Check if there's a stored auth provider - user might have been logged in before
        const storedProvider = localStorage.getItem(
          LocalStorageKeys.AuthProvider,
        );
        if (storedProvider) {
          console.log(
            "[HomeComponent] Found stored auth provider but no auth state - clearing stale auth",
          );
          localStorage.removeItem(LocalStorageKeys.AuthProvider);

          // PostHog: Track auth timeout
          this.posthog.captureEvent("auth_state_timeout", {
            had_stored_provider: true,
            stored_provider: storedProvider,
          });
        }

        // Redirect to login
        this.router.navigateByUrl("/login");
      }
    }, 10000);

    // Subscribe to NEW Google Identity Services auth state (GIS)
    // This handles users who logged in via the new GIS flow
    this.googleAuthService.authState$.subscribe(
      async (gisUser: GoogleUser | null) => {
        // Clear the timeout since auth state has fired
        clearTimeout(authTimeout);

        if (gisUser && !this.hasProcessedAuthState && !this.isLoggingOut) {
          console.log("[HomeComponent] GIS authState fired", {
            userId: gisUser.id,
            email: gisUser.email,
            provider: gisUser.provider,
          });

          await this.handleGoogleAuthUser(gisUser);
        } else if (!gisUser && !this.isLoggingOut) {
          console.log(
            "[HomeComponent] GIS authState fired with null user (not authenticated)",
          );

          // Only redirect to login if:
          // 1. GoogleAuthService has finished initializing (checked stored credentials)
          // 2. We haven't already processed authentication in this session
          // 3. httpService is also not logged in (double-check against race conditions)
          // This prevents premature redirect when BehaviorSubject emits initial null value
          if (
            this.googleAuthService.isReady() &&
            !this.hasProcessedAuthState &&
            !this.httpService.loggedIn
          ) {
            console.log(
              "[HomeComponent] No authenticated user after initialization - redirecting to login",
            );
            this.router.navigateByUrl("/login");
          } else {
            console.log(
              "[HomeComponent] Waiting for auth initialization or already logged in via httpService",
            );
          }
        } else if (this.isLoggingOut) {
          console.log(
            "[HomeComponent] GIS authState fired during logout, ignoring",
          );
        }
      },
    );

    // Also initialize GoogleAuthService to check for stored credentials
    this.googleAuthService.initialize().then(() => {
      console.log("[HomeComponent] GoogleAuthService initialized");
    });

    this.resize();

    // Set sidebar state based on screen size BEFORE any rendering
    // Start with sidebar closed (default), open it for desktop
    if (this.windowWidth > 768) {
      this.rightSidebarOpen = true;
    }

    this.httpService.setBCanvas(this.bcanvas);

    this.bcanvas.onCurrentBeadChanged.subscribe(
      async (currentBead: PaletteDetailModel) => {
        this.currentBeadColor = currentBead ? currentBead.color : null;

        // Automatically switch to draw bead tool after picking a color
        if (this.selectedEditMode === "pick-bead") {
          this.bcanvas.setEditMode(EditMode.AddBead);
        }
      },
    );

    // Subscribe to edit mode changes (from keyboard shortcuts)
    this.bcanvas.onEditModeChanged.subscribe((editMode: EditMode) => {
      // Map EditMode enum to the string value used by mat-button-toggle
      switch (editMode) {
        case EditMode.AddBead:
          this.selectedEditMode = "add-bead";
          break;
        case EditMode.Fill:
          this.selectedEditMode = "fill";
          break;
        case EditMode.RemoveBead:
          this.selectedEditMode = "remove-bead";
          break;
        case EditMode.RemoveFill:
          this.selectedEditMode = "remove-fill";
          break;
        case EditMode.PickBead:
          this.selectedEditMode = "pick-bead";
          break;
        case EditMode.Pan:
          this.selectedEditMode = "pan";
          break;
      }
      // Trigger change detection to update the UI
      this.changeDetectorRef.detectChanges();
    });

    // Subscribe to palette mismatch errors (when user tries to place incompatible beads)
    this.paletteMismatchSubscription = this.bcanvas.onPaletteMismatchError.subscribe(
      (patternPaletteId: number) => {
        const paletteName = getBuiltInPaletteName(
          patternPaletteId,
          this.paletteRegistry,
        );
        const errorMessage = Utility.formatString(
          this.httpService.translation.PaletteMismatchError,
          paletteName,
        );
        Utility.showNotification(
          this._snackBar,
          errorMessage,
          NotificationTypeEnum.Warning,
        );
      },
    );

    // Sidebar state is already set correctly above based on screen size
    // No need to close it here anymore

    // PERFORMANCE TRACKING: Track HomeComponent initialization time
    const ngOnInitTime = performance.now() - ngOnInitStart;
    const totalHomeLoadTime = performance.now() - this.homeComponentLoadStart;

    // Set mobile mode for onboarding service (for showing floating hints on mobile)
    this.onboardingService.setMobileMode(this.deviceService.isMobile());

    this.posthog.captureEvent("home_component_loaded", {
      ng_on_init_time_ms: Math.round(ngOnInitTime),
      total_home_load_time_ms: Math.round(totalHomeLoadTime),
      is_production: environment.production,
      user_logged_in: this.httpService.loggedIn,
      device_type: this.deviceService.getDeviceInfo().deviceType,
      is_mobile: this.deviceService.isMobile(),
      window_width: this.windowWidth,
      window_height: this.windowHeight,
    });
  }

  ngAfterViewInit(): void {
    // PERFORMANCE TRACKING: Mark when app is fully interactive
    const timeToInteractive = performance.now();

    // Calculate full page load time (from navigation start to interactive)
    const navigationStart =
      performance.timing?.navigationStart || performance.timeOrigin || 0;
    const pageLoadToInteractive =
      navigationStart > 0 ? Date.now() - navigationStart : timeToInteractive;

    // Cap TTI at 60 seconds to filter out background tab noise
    // (Users who switch tabs and come back hours later would skew the metric)
    const maxReasonableTTI = 60000; // 60 seconds
    const cappedTTI = Math.min(timeToInteractive, maxReasonableTTI);
    const wasCapped = timeToInteractive > maxReasonableTTI;

    // Track full page load time (includes login flow, redirects, etc.)
    this.posthog.captureEvent("page_load_to_interactive", {
      time_ms: Math.round(pageLoadToInteractive),
      is_slow: pageLoadToInteractive > 10000, // >10s is slow for full page load
      device_type: this.deviceService.getDeviceInfo().deviceType,
      is_mobile: this.deviceService.isMobile(),
      browser: this.deviceService.browser,
      os: this.deviceService.os,
      navigation_start: navigationStart,
    });

    this.posthog.captureEvent("app_interactive", {
      time_to_interactive_ms: Math.round(cappedTTI),
      is_slow: cappedTTI > 5000,
      was_capped: wasCapped,
      actual_uncapped_ms: wasCapped ? Math.round(timeToInteractive) : undefined,
      device_type: this.deviceService.getDeviceInfo().deviceType,
      is_mobile: this.deviceService.isMobile(),
      browser: this.deviceService.browser,
      os: this.deviceService.os,
    });

    // Log slow TTI to console for debugging
    if (cappedTTI > 5000 && !wasCapped) {
      console.warn(
        "[Performance] Slow Time-to-Interactive detected:",
        Math.round(cappedTTI),
        "ms",
      );
    } else if (wasCapped) {
      console.warn(
        "[Performance] TTI capped - likely background tab:",
        Math.round(timeToInteractive),
        "ms → capped to",
        maxReasonableTTI,
        "ms",
      );
    }

    // Log slow full page load
    if (pageLoadToInteractive > 10000) {
      console.warn(
        "[Performance] Slow Full Page Load detected:",
        Math.round(pageLoadToInteractive),
        "ms",
      );
    }

    // PERFORMANCE TRACKING: Track first user interaction delay
    const trackFirstInteraction = (event: Event) => {
      if (!this.firstInteractionTracked) {
        this.firstInteractionTracked = true;
        const interactionDelay = performance.now() - timeToInteractive;

        this.posthog.captureEvent("first_interaction", {
          time_since_interactive_ms: Math.round(interactionDelay),
          interaction_type: event.type,
          total_time_to_first_interaction_ms: Math.round(performance.now()),
          device_type: this.deviceService.getDeviceInfo().deviceType,
        });

        console.log(
          "[Performance] First interaction:",
          event.type,
          "after",
          Math.round(interactionDelay),
          "ms",
        );
      }
    };

    // Listen for first interaction
    ["click", "touchstart", "keydown"].forEach((eventType) => {
      document.addEventListener(eventType, trackFirstInteraction, {
        once: true,
        passive: true,
      });
    });

    // Check if user has a saved path but hasn't completed onboarding
    const savedPath = this.onboardingService.getOnboardingPath();
    const activated = localStorage.getItem("onboarding_checklist_completed");

    if (savedPath && activated !== "true") {
      // User has selected a path before but hasn't completed onboarding
      // Restore their onboarding state
      if (this.checklistComponent) {
        setTimeout(() => {
          this.checklistComponent.setOnboardingPath(savedPath);
          this.checklistComponent.completeStep("signin");
        }, 500);
      }
    }

    // Initialize pattern position for mobile users
    // This ensures the grid is visible on load without requiring scroll
    // IMPORTANT: Only reset position if welcome modal won't be shown
    // If welcome modal will be shown, let it handle the position reset after close
    const hasSeenWelcome = localStorage.getItem(
      LocalStorageKeys.OnboardingWelcomeCompleted,
    );

    if (hasSeenWelcome) {
      // User has seen welcome before - safe to reset position now
      this.resize();
      if (this.beadCanvasComponent) {
        this.beadCanvasComponent.resizeCanvasContainer();
        this.beadCanvasComponent.resetPatternPosition();
      }
      // Initial load complete - allow normal scrolling from now on
      this.isInitialLoad = false;
    }
    // If welcome modal will be shown, modal close handler will handle positioning

    this.showWelcomeModalIfFirstTime();

    // Prevent ExpressionChangedAfterItHasBeenCheckedError for isOnboardingVisible
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Handle authentication from Google Identity Services (GIS)
   * This is called when a user authenticates via the new GIS flow
   */
  private async handleGoogleAuthUser(gisUser: GoogleUser): Promise<void> {
    console.log("[HomeComponent] handleGoogleAuthUser called", {
      userId: gisUser.id,
      email: gisUser.email,
      provider: gisUser.provider,
    });

    this.hasProcessedAuthState = true;

    // Set user data in httpService (compatibility with existing code)
    this.httpService.socialUser = {
      id: gisUser.id,
      email: gisUser.email,
      name: gisUser.name,
      firstName: gisUser.firstName,
      lastName: gisUser.lastName,
      photoUrl: gisUser.photoUrl,
      provider: gisUser.provider,
      idToken: gisUser.idToken,
      authToken: gisUser.authToken || "",
      authorizationCode: "",
      response: {},
    } as any;
    this.httpService.loggedIn = true;
    this.httpService.userId = gisUser.id;

    console.log("[HomeComponent] GIS User ID:", this.httpService.userId);

    // PostHog: Identify user
    this.posthog.identifyUser(gisUser.id, {
      email: gisUser.email,
      name: gisUser.name,
      provider: gisUser.provider,
      auth_method: "gis",
    });

    // PostHog: Set device-specific properties
    this.posthog.setSuperProperties({
      device_type: this.deviceService.getDeviceInfo().deviceType,
      is_mobile: this.deviceService.isMobile(),
      is_tablet: this.deviceService.isTablet(),
      is_desktop: this.deviceService.isDesktop(),
      browser: this.deviceService.browser,
      os: this.deviceService.os,
    });

    // Check if user has already activated
    const posthogInstance = this.posthog.getPosthogInstance();
    if (posthogInstance) {
      const hasActivated = posthogInstance.get_property("has_activated");
      if (hasActivated) {
        this.hasCompletedFirstSave = true;
        console.log(
          "[Activation] User has already activated in a previous session",
        );
      }

      const hasCreatedFirstPattern = posthogInstance.get_property(
        "has_created_first_pattern",
      );
      if (hasCreatedFirstPattern) {
        this.hasCreatedPattern = true;
        console.log(
          "[Activation] User has already created first pattern in a previous session",
        );
      }
    }

    const loginSuccess = await this.login();

    if (loginSuccess) {
      console.log("[HomeComponent] GIS login() completed successfully");
    } else {
      console.error("[HomeComponent] GIS login() failed");
    }
  }

  private async login(): Promise<boolean> {
    const maxRetries = 3;
    const retryDelays = [1000, 2000, 4000]; // Exponential backoff

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log(
          `[HomeComponent] Login attempt ${attempt + 1}/${maxRetries}`,
        );
        await this.httpService.login();

        // Login successful - process temporary pattern if any
        if (this.httpService.user?.saveTemporaryPatternId !== null) {
          let patternModel = await this.httpService.getPattern(
            this.httpService.user.saveTemporaryPatternId,
          );

          if (patternModel != null) {
            patternModel.patternData =
              this.httpService.user.saveTemporaryPatternData;

            if (patternModel.patternData != null) {
              await this.openPattern(patternModel);
              this.httpService.dirtyLastActionId();
            }
          } else {
            let patternModel = <PatternModel>{
              patternId: this.currentPattern
                ? this.currentPattern.patternId
                : 0,
              name: "",
              description: "",
              categoryId: 0,
              categoryName: "",
              patternData: this.httpService.user.saveTemporaryPatternData,
              imageFilename: this.httpService.user.imageFilename,
              userId: this.httpService.userId,
              createdOn: null,
              lastUpdateOn: null,
            };

            if (patternModel.patternData != null) {
              await this.openPattern(patternModel);
              this.httpService.dirtyLastActionId();
            }
          }
        }

        return true; // Login successful
      } catch (error) {
        console.error(
          `[HomeComponent] Login attempt ${attempt + 1} failed:`,
          error,
        );

        // PostHog: Track login failure
        this.posthog.captureEvent("home_login_failed", {
          attempt: attempt + 1,
          error_message: error?.message || "Unknown error",
          will_retry: attempt < maxRetries - 1,
        });

        if (attempt < maxRetries - 1) {
          // Wait before retrying
          console.log(
            `[HomeComponent] Waiting ${retryDelays[attempt]}ms before retry...`,
          );
          await new Promise((resolve) =>
            setTimeout(resolve, retryDelays[attempt]),
          );
        }
      }
    }

    // All retries failed - redirect to login page
    console.error(
      "[HomeComponent] All login attempts failed, redirecting to login page",
    );

    // PostHog: Track total login failure
    this.posthog.captureEvent("home_login_all_retries_failed", {
      total_attempts: maxRetries,
    });

    // Clear auth state to allow fresh login
    localStorage.removeItem(LocalStorageKeys.AuthProvider);
    this.httpService.loggedIn = false;
    this.httpService.socialUser = null;
    this.httpService.userId = null;

    // Show error message to user
    this._snackBar.open(
      "Unable to connect to server. Please try logging in again.",
      "Close",
      {
        duration: 5000,
        panelClass: ["error-snackbar"],
      },
    );

    // Navigate to login page
    this.router.navigateByUrl("/login");
    return false;
  }

  /**
   * Show welcome modal on first visit to guide user to their first action
   */
  private showWelcomeModalIfFirstTime(): void {
    // Check if user has already seen the welcome modal
    const hasSeenWelcome = localStorage.getItem(
      LocalStorageKeys.OnboardingWelcomeCompleted,
    );

    if (!hasSeenWelcome) {
      // Delay to ensure login completes and translations are loaded
      setTimeout(() => {
        console.log(
          "[HomeComponent] Opening welcome modal with translation:",
          this.httpService.user?.language,
        );
        const dialogRef = this.dialog.open(WelcomeOnboardingModalComponent, {
          width: "500px",
          maxWidth: "90vw",
          disableClose: true,
          panelClass: "welcome-onboarding-dialog",
        });

        dialogRef.afterClosed().subscribe(async (choice: OnboardingPath) => {
          // Sidebar state is already correct (closed on mobile, open on desktop)
          // Just need to resize and position the pattern after modal closes
          this.resize();
          if (this.beadCanvasComponent) {
            this.beadCanvasComponent.resizeCanvasContainer();
            this.beadCanvasComponent.resetPatternPosition();
          }
          // Initial load complete - allow normal scrolling from now on
          this.isInitialLoad = false;

          if (choice) {
            // Set the onboarding path
            this.onboardingService.setOnboardingPath(choice);

            // Set the path in the checklist component
            if (this.checklistComponent) {
              this.checklistComponent.setOnboardingPath(choice);
              // Complete the first checklist step (signed in)
              this.checklistComponent.completeStep("signin");
            }

            // Handle different onboarding choices
            if (choice === "upload") {
              // Skip pattern setup modal and go straight to upload hint
              this.onboardingService.showUploadImageHint();
            } else if (choice === "video") {
              // Open video tutorial modal (pattern setup shown after video closes)
              this.openVideoTutorial("onboarding");
            }
          } else {
            // User skipped/cancelled onboarding - still show pattern setup
            await this.maybePromptInitialPatternSetup();
          }
        });
      }, 1000);
    } else {
      // User has seen welcome before, just complete signin step
      if (this.checklistComponent) {
        setTimeout(() => {
          this.checklistComponent.completeStep("signin");
        }, 500);
      }
    }
  }

  /**
   * Open video tutorial modal for onboarding, toolbar, or help panel
   */
  private openVideoTutorial(
    source: "onboarding" | "toolbar" | "help-panel" = "onboarding",
  ): void {
    // Use window.innerWidth for responsive detection (more reliable than user agent)
    const isDesktop = window.innerWidth >= 768;
    const videoUrl = isDesktop
      ? "assets/videos/tutorial-desktop.mp4"
      : "assets/videos/tutorial-mobile.mp4";
    const videoTitle = isDesktop
      ? "Quick Tutorial - Bead Pattern Maker"
      : "Mobile Tutorial - Bead Pattern Maker";

    // Track video tutorial opened from menu/toolbar
    this.posthog.captureEvent("video_tutorial_menu_clicked", {
      device_type: isDesktop ? "desktop" : "mobile",
      screen_width: window.innerWidth,
      video_url: videoUrl,
      source,
    });

    const dialogConfig: any = {
      data: {
        videoUrl,
        title: videoTitle,
        useHtml5: true,
      },
      panelClass: "video-player-dialog",
      autoFocus: false,
    };

    if (isDesktop) {
      // Calculate optimal dimensions while maintaining 16:9 aspect ratio
      dialogConfig.width = "95vw";
      dialogConfig.height = "95vh";
      dialogConfig.maxWidth = "95vw";
      dialogConfig.maxHeight = "95vh";
    } else {
      dialogConfig.width = "100vw";
      dialogConfig.height = "100vh";
      dialogConfig.maxWidth = "100vw";
      dialogConfig.maxHeight = "100vh";
    }

    const dialogRef = this.dialog.open(VideoPlayerModalComponent, dialogConfig);

    // Only track tutorial completion for onboarding flow
    if (source === "onboarding") {
      dialogRef.beforeClosed().subscribe(() => {
        const stats = dialogRef.componentInstance?.getWatchStats?.();
        this.handleVideoTutorialFlowCompletion(stats);
      });
    }
  }

  /**
   * Show position guide modal after image upload
   */
  private showPositionGuideModal(): void {
    // Hide the upload image hint since image is already uploaded
    this.onboardingService.hideAllHints();

    const dialogRef = this.dialog.open(PositionImageGuideModal, {
      width: "650px",
      maxWidth: "95vw",
      disableClose: false,
      hasBackdrop: false,
      panelClass: "position-guide-dialog",
    });

    dialogRef.afterClosed().subscribe((gotIt: boolean) => {
      // User closed the position guide modal
      // Now complete the upload_image step which will trigger the next step
      if (this.checklistComponent) {
        this.checklistComponent.completeStep("upload_image");
      }

      if (gotIt) {
        // User clicked "Got It", show transfer hint (for desktop)
        this.onboardingService.showMoveAndScaleImageHint();
      } else {
        // User closed modal, still show transfer hint (for desktop)
        this.onboardingService.showMoveAndScaleImageHint();
      }
    });
  }

  public onOnboardingChecklistClosed(): void {
    this.onboardingService.hideAllHints();
  }

  /**
   * Handle next step change - auto-open menu for first step, then wait for user clicks
   * Called when a checklist step is completed and the next step becomes active
   */
  public onNextStepChanged(action: string | null): void {
    console.log("[HomeComponent] Next step changed to:", action);

    // Only auto-open on mobile
    if (!this.deviceService.isMobile() || !action || !this.appToolbar) {
      return;
    }

    // Auto-open menu ONLY for the first step (upload_image)
    // For subsequent steps, user must click the checklist step to learn where menu items are
    if (action === "upload_image") {
      // First step: Auto-open to help user get started
      this.appToolbar.openMenuForChecklist("image", "insert_image");
    }
    // For 'convert_pattern' and 'save' steps, the checklist will highlight the step
    // and wait for user to click it, teaching them where to find the menu items
  }

  /**
   * Handle checklist step click - trigger the appropriate action
   * On mobile: Opens menu to show user where to click
   * On desktop: Directly triggers the action
   */
  public onChecklistStepClicked(action: string): void {
    console.log("[HomeComponent] Checklist step clicked:", action);

    const isMobile = this.deviceService.isMobile();

    switch (action) {
      case "upload_image":
        if (isMobile && this.appToolbar) {
          // Mobile: Open the Image menu and highlight "Insert Image"
          this.appToolbar.openMenuForChecklist("image", "insert_image");
        } else if (this.appToolbar) {
          // Desktop: Directly trigger file upload
          this.appToolbar.triggerFileUpload();
        }
        break;

      case "convert_pattern":
        if (isMobile && this.appToolbar) {
          // Mobile: Open the Image menu and highlight "Transfer Image"
          this.appToolbar.openMenuForChecklist("image", "transfer_image");
        } else {
          // Desktop: Directly open the transfer modal
          this.showTransferImage();
        }
        break;

      case "save":
        if (isMobile && this.appToolbar) {
          // Mobile: Open the File menu and highlight "Save"
          this.appToolbar.openMenuForChecklist("file", "save");
        } else {
          // Desktop: Directly trigger save
          this.onToolbarActionFired(<ToolbarActionFired>{
            actionType: ToolbarActionFiredTypeEnum.Save,
          });
        }
        break;

      default:
        console.warn("[HomeComponent] Unknown checklist action:", action);
    }
  }

  /**
   * Handle mobile save button click
   */
  public onMobileSaveClick(): void {
    this.posthog.captureEvent("mobile_save_fab_clicked", {
      device_type: this.deviceService.getDeviceInfo().deviceType,
    });

    // Trigger save action
    this.onToolbarActionFired(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.Save,
    });
  }

  /**
   * Show mobile save button on mobile devices after user makes changes
   */
  private updateMobileSaveButtonVisibility(): void {
    // Only show on mobile/tablet devices
    if (!this.deviceService.isMobile() && !this.deviceService.isTablet()) {
      this.showMobileSaveButton = false;
      return;
    }

    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      // Show when user can save (has made changes)
      this.showMobileSaveButton = this.httpService.canSavePattern();

      // Show pulse animation when first showing
      if (this.showMobileSaveButton && !this.showMobileSavePulse) {
        this.showMobileSavePulse = true;
        // Stop pulsing after 6 seconds
        setTimeout(() => {
          this.showMobileSavePulse = false;
        }, 6000);
      }
    }, 0);
  }

  private async handleVideoTutorialFlowCompletion(
    stats?: VideoWatchStats,
  ): Promise<void> {
    if (this.isOnboardingTutorialCompleted()) {
      return;
    }

    const watchedPercentage = Math.round(stats?.maxWatchedPercentage ?? 0);

    this.posthog.captureEvent("onboarding_tutorial_completed", {
      tutorial_type: "video",
      timestamp: new Date().toISOString(),
      watched_percentage: watchedPercentage,
      fully_watched: stats?.fullyWatched ?? false,
      started_playing: stats?.startedPlaying ?? false,
    });

    this.markOnboardingTutorialCompleted("video");

    // Video path doesn't use checklist - mark as completed directly
    if (this.checklistComponent) {
      localStorage.setItem(
        LocalStorageKeys.OnboardingChecklistCompleted,
        "true",
      );
      this.checklistComponent.isVisible = false;
    }

    // Show pattern setup modal after video tutorial
    await this.maybePromptInitialPatternSetup();
  }

  private async maybePromptInitialPatternSetup(): Promise<boolean> {
    if (
      localStorage.getItem(LocalStorageKeys.PatternSetupPrompted) === "true"
    ) {
      return false;
    }

    const setup = await this.promptForPatternSetup("onboarding");
    if (setup) {
      await this.newPattern(setup);
      localStorage.setItem(LocalStorageKeys.PatternSetupPrompted, "true");
      return true; // User confirmed the setup
    }

    localStorage.setItem(LocalStorageKeys.PatternSetupPrompted, "true");
    return false; // User cancelled the setup
  }

  private markOnboardingTutorialCompleted(type: "video" | "interactive"): void {
    if (this.isOnboardingTutorialCompleted()) {
      return;
    }

    localStorage.setItem(LocalStorageKeys.OnboardingTutorialCompleted, "true");
    this.posthog.setSuperProperty("onboarding_tutorial_completed", true);
    this.posthog.setSuperProperty("onboarding_tutorial_type", type);
  }

  private isOnboardingTutorialCompleted(): boolean {
    return (
      localStorage.getItem(LocalStorageKeys.OnboardingTutorialCompleted) ===
      "true"
    );
  }

  private resize() {
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;

    const smallToolbarVertical = this.windowWidth > this.minSideToolbarWidth;

    // Calculate toolbar height first (changes at 430px breakpoint)
    this.appToolbarHeight =
      this.windowWidth > this.appToolbar.minMainMenuWidth ? 79 : 43;

    // Calculate overhead for sidebar content (tab header, padding, form fields, etc.)
    // We have THREE different modes based on TWO breakpoints:
    // 1. Desktop (>510px): 79px toolbar, vertical floating toolbar → 146/196 overhead
    // 2. Medium (431-510px): 79px toolbar, horizontal floating toolbar at bottom → 171/231 overhead
    // 3. Mobile (≤430px): 43px toolbar, horizontal floating toolbar at bottom → 207/267 overhead
    let patternOverhead: number;
    let paletteOverhead: number;

    if (smallToolbarVertical) {
      // Desktop mode: vertical floating toolbar
      patternOverhead = 140;
    } else if (this.windowWidth > this.appToolbar.minMainMenuWidth) {
      // Medium mode: desktop toolbar but horizontal floating toolbar at bottom
      patternOverhead = 145;
    } else {
      // Mobile mode: mobile toolbar and horizontal floating toolbar at bottom
      patternOverhead = 145;
    }

    paletteOverhead = patternOverhead + 50;

    // Dynamically calculate heights based on toolbar height + overhead
    this.patternListHeight =
      this.windowHeight - this.appToolbarHeight - patternOverhead;
    this.paletteListHeight =
      this.windowHeight - this.appToolbarHeight - paletteOverhead;

    if (smallToolbarVertical) {
      // Vertically aligned
      this.smallToolbarLeft = 0;
      this.smallToolbarTop = this.appToolbarHeight + 10;
    } else {
      // Horizontally aligned
      this.smallToolbarLeft = this.windowWidth / 2;
      this.smallToolbarTop = this.windowHeight - 65;
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.resize();
  }

  public async onToolbarActionFired(actionFired: ToolbarActionFired) {
    switch (actionFired.actionType) {
      case ToolbarActionFiredTypeEnum.New:
        // PostHog: Track new pattern action
        this.posthog.captureEvent("new_pattern_clicked");
        if (this.httpService.canSavePattern()) {
          Utility.confirmMessage(
            this.dialog,
            this.httpService.getTransation().Warning,
            this.httpService.getTransation().ConfirmUnsavedPattern,
            async (): Promise<void> => {
              await this.handleNewPatternRequest();
            },
          );
        } else {
          await this.handleNewPatternRequest();
        }
        break;

      case ToolbarActionFiredTypeEnum.Open:
        if (this.httpService.canSavePattern()) {
          Utility.confirmMessage(
            this.dialog,
            this.httpService.getTransation().Warning,
            this.httpService.getTransation().ConfirmUnsavedPattern,
            async (): Promise<void> => {
              this.openPatternWithDialog();
            },
          );
        } else {
          this.openPatternWithDialog();
        }
        break;

      case ToolbarActionFiredTypeEnum.Save:
        this.savePatternWithDialog(false, false);
        break;

      case ToolbarActionFiredTypeEnum.SaveAs:
        this.savePatternWithDialog(true, false);
        break;

      case ToolbarActionFiredTypeEnum.Print:
        this.showPrintPattern();
        break;

      case ToolbarActionFiredTypeEnum.ToggleImageVisibility:
        this.bcanvas.setPatternImageVisible(
          !this.bcanvas.isPatternImageVisible(),
        );
        break;

      case ToolbarActionFiredTypeEnum.ToggleRealisticBeadsEnabled:
        this.bcanvas.setRealisticBeadsEnabled(
          !this.bcanvas.isRealisticBeadsEnabled(),
        );
        break;

      case ToolbarActionFiredTypeEnum.UploadImage:
        // PostHog: Track image upload
        this.posthog.captureEvent("image_upload_clicked");
        let files = actionFired.parameters;
        this.uploadFiles(files);
        break;

      case ToolbarActionFiredTypeEnum.TransferImage:
        this.showTransferImage();
        break;

      case ToolbarActionFiredTypeEnum.TransformPattern:
        this.showTransformPattern();
        break;

      case ToolbarActionFiredTypeEnum.PaletteEditor:
        this.showPaletterEditor();
        break;

      case ToolbarActionFiredTypeEnum.ShiftLeft:
        this.bcanvas.shiftPattern(ShiftTypeEnum.Left);
        break;

      case ToolbarActionFiredTypeEnum.ShiftRight:
        this.bcanvas.shiftPattern(ShiftTypeEnum.Right);
        break;

      case ToolbarActionFiredTypeEnum.ShiftUp:
        this.bcanvas.shiftPattern(ShiftTypeEnum.Up);
        break;

      case ToolbarActionFiredTypeEnum.ShiftDown:
        this.bcanvas.shiftPattern(ShiftTypeEnum.Down);
        break;

      case ToolbarActionFiredTypeEnum.RotateLeft:
        this.bcanvas.rotatePattern(RotateDirectionEnum.Left);
        break;

      case ToolbarActionFiredTypeEnum.RotateRight:
        this.bcanvas.rotatePattern(RotateDirectionEnum.Right);
        break;

      case ToolbarActionFiredTypeEnum.FlipVertically:
        this.bcanvas.flipPattern(FlipDirectionEnum.Vertical);
        break;

      case ToolbarActionFiredTypeEnum.FlipHorizontally:
        this.bcanvas.flipPattern(FlipDirectionEnum.Horizontal);
        break;

      case ToolbarActionFiredTypeEnum.RotateImageLeft:
        await this.httpService.rotateImage(RotateDirectionEnum.Left);
        break;

      case ToolbarActionFiredTypeEnum.RotateImageRight:
        await this.httpService.rotateImage(RotateDirectionEnum.Right);
        break;
      case ToolbarActionFiredTypeEnum.ShowSubscription:
        this.showSubscription();
        break;

      case ToolbarActionFiredTypeEnum.OpenPatternReader:
        this.showPatternReader();
        break;

      case ToolbarActionFiredTypeEnum.ToggleRightSidebar:
        this.toggleRightSidebar();
        break;

      case ToolbarActionFiredTypeEnum.ToggleHelpPanel:
        this.toggleHelpPanel();
        break;

      // Old welcome tour removed - replaced with new onboarding system
      // case ToolbarActionFiredTypeEnum.StartWelcomeTour:
      //   break;

      case ToolbarActionFiredTypeEnum.VideoTutorial:
        // Open video tutorial in modal instead of new window
        this.openVideoTutorial("toolbar");
        break;
    }
  }

  public async newPattern(setup?: PatternSetupResult) {
    // Reset pattern creation tracking (actual creation happens on first edit)
    this.hasCreatedPattern = false;

    this.lastActionId = 0;
    this.currentPattern = null;
    this.lastTemporaryPatternData = null;
    if (this.httpService.user) {
      this.httpService.user.imageFilename = null;
    }
    this.beadCanvasComponent.resetZoom();
    this.beadCanvasComponent.resetPatternPosition();
    this.beadCanvasComponent.resetRotation();
    this.bcanvas.controller.newPattern();
    this.bcanvas.repaint();
    this.appToolbar.refreshRotatedTooltip();
    this.bcanvas.scrollCanvasContainerToPatternPosition();
    this.bcanvas.controller.resetUndoRedo();
    this.bcanvas.setPatternImage(undefined);
    this.changeDetectorRef.detectChanges();

    if (setup) {
      this.applyPatternSetup(setup);
    }
  }

  private applyPatternSetup(setup: PatternSetupResult): void {
    const selectedStitch = this.stitchs.find(
      (s) => s.stitchId === setup.stitchId,
    );

    if (selectedStitch) {
      this.bcanvas.controller.changeStitch(selectedStitch.stitchId);
    }

    this.bcanvas.setCols(setup.columns);
    this.bcanvas.setRows(setup.rows);
    this.bcanvas.repaint();
    this.changeDetectorRef.detectChanges();
  }

  private async handleNewPatternRequest(): Promise<void> {
    const setup = await this.promptForPatternSetup("new");
    if (setup) {
      await this.newPattern(setup);
    }
  }

  private async promptForPatternSetup(
    trigger: "new" | "onboarding",
  ): Promise<PatternSetupResult | undefined> {
    const dialogRef = this.dialog.open(NewPatternSetupModalComponent, {
      maxWidth: "95vw",
      maxHeight: "95vh",
      autoFocus: true,
      restoreFocus: true,
      data: {
        stitches: this.stitchs,
        unitOfMeasure:
          this.httpService.user?.unitOfMeasure ?? UnitOfMeasureEnum.Inch,
        trigger,
      },
    });

    return (await dialogRef.afterClosed().toPromise()) as
      | PatternSetupResult
      | undefined;
  }

  public async openPatternWithDialog() {
    const dialogRef = this.dialog.open(PatternExplorerModal, {
      maxWidth: "700px",
      maxHeight: "700px",
      width: "90%",
      height: "90%",
      data: {
        mode: PatternExplorerModalMode.Open,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      let pattern = <PatternModel>result;

      if (pattern) {
        this.openPattern(pattern);
        this.lastTemporaryPatternData = null;
      }
    });
  }

  public async openPattern(pattern: PatternModel) {
    const startTime = Date.now();

    // Reset pattern creation tracking (pattern already exists, so it's already "created")
    this.hasCreatedPattern = true;

    this.currentPattern = pattern;

    let patternBead = this.httpService.deserializePatternBead(
      pattern.patternData,
    );

    var selectedStitch = this.stitchs.find(
      (x) => x.stitchId === patternBead.stitchId,
    );

    if (selectedStitch === undefined) {
      selectedStitch = this.stitchs[0];
    }

    if (this.currentPattern.imageFilename) {
      let patternImage = new Image();
      patternImage.src =
        "Assets/Images/" +
        this.httpService.user.userId +
        "/" +
        this.currentPattern.imageFilename +
        "?" +
        new Date().getTime();
      this.bcanvas.patternImageLoaded = false;
      patternImage.onload = async () => {
        this.bcanvas.patternImageLoaded = true;
      };

      await this.refreshCanvas(pattern, patternImage);
    } else {
      await this.refreshCanvas(pattern, null);
    }

    const loadTime = Date.now() - startTime;
    const patternComplexity = this.getPatternComplexity();

    // PostHog: Track pattern opened (existing pattern loaded)
    this.posthog.captureEvent("pattern_opened", {
      pattern_id: pattern.patternId,
      pattern_name: pattern.name,
      pattern_width: this.bcanvas.getCols(),
      pattern_height: this.bcanvas.getRows(),
      total_cells: this.bcanvas.getCols() * this.bcanvas.getRows(),
      has_image: !!(this.currentPattern && this.currentPattern.imageFilename),
      load_time_ms: loadTime,
    });

    // PostHog: Track pattern load with performance data (legacy event - keeping for compatibility)
    this.posthog.captureEvent("pattern_loaded", {
      load_time_ms: loadTime,
      pattern_width: this.bcanvas.getCols(),
      pattern_height: this.bcanvas.getRows(),
      total_cells: this.bcanvas.getCols() * this.bcanvas.getRows(),
      has_image: !!(this.currentPattern && this.currentPattern.imageFilename),
      pattern_complexity: patternComplexity,
      is_slow: loadTime > 3000, // Flag if > 3 seconds
    });

    // Track slow pattern loads separately
    if (loadTime > 3000) {
      this.posthog.captureEvent("slow_pattern_load_detected", {
        load_time_ms: loadTime,
        total_cells: this.bcanvas.getCols() * this.bcanvas.getRows(),
        pattern_complexity: patternComplexity,
      });
    }
  }

  public async saveTemporaryPattern() {
    try {
      let patternData = JSON.stringify(this.bcanvas.getData());

      if (this.lastTemporaryPatternData != patternData) {
        let pattern = <PatternModel>{
          patternId: this.currentPattern ? this.currentPattern.patternId : 0,
          name: "",
          description: "",
          categoryId: 0,
          categoryName: "",
          patternData: patternData,
          userId: this.httpService.userId,
          createdOn: null,
          lastUpdateOn: null,
        };
        this.savePattern(pattern, true);
        this.lastTemporaryPatternData = patternData;
      }
    } catch (error) {
      // Handle JSON.stringify errors (e.g., RangeError for very large patterns)
      console.error("Error saving temporary pattern:", error);

      // Track error in PostHog
      this.posthog.captureEvent("pattern_save_error", {
        error_type: error.name,
        error_message: error.message,
        pattern_size_estimate: `${this.bcanvas.getCols()}x${this.bcanvas.getRows()}`,
        total_cells: this.bcanvas.getCols() * this.bcanvas.getRows(),
      });

      // Show user-friendly error
      Utility.showNotification(
        this._snackBar,
        "Pattern is too large to save. Please reduce the size.",
        NotificationTypeEnum.Warning,
      );
    }
  }

  public async savePatternWithDialog(isSaveAs: boolean, isTemporary: boolean) {
    if (!isTemporary) {
      await this.saveTemporaryPattern();

      let hasName = this.currentPattern && this.currentPattern.name;

      if (await this.checkSubscriptionAvailable(isSaveAs || !hasName, false)) {
        let showDialog = true;

        if (!isSaveAs) {
          if (hasName) {
            this.savePattern(this.currentPattern, false);
            showDialog = false;
          }
        }

        if (showDialog) {
          // Hide save hint when modal opens
          this.onboardingService.hideAllHints();
          localStorage.setItem("hint_save_reminder_shown", "true");

          const dialogRef = this.dialog.open(AddEditPatternModal, {
            width: "510px",
            data: {
              mode: AddEditPatternModalMode.Add,
              pattern: null,
            },
          });

          dialogRef.afterClosed().subscribe(async (result) => {
            let pattern = <PatternModel>result;

            if (pattern) {
              this.savePattern(pattern, false);

              // Complete checklist step: save (for both paths)
              if (this.checklistComponent) {
                this.checklistComponent.completeStep("save");
              }
            }
          });
        }
      } else {
        this.pleaseWait = true;
        await this.showSubscription();
        this.pleaseWait = false;
      }
    } else {
      await this.saveTemporaryPattern();
    }
  }

  public async checkSubscriptionAvailable(
    isSaveAs: boolean,
    isPrint: boolean,
  ): Promise<boolean> {
    let ok = false;

    if (environment.production) {
      if (
        this.httpService.user.subscriptionType !== SubscriptionTypeEnum.None
      ) {
        const date = this.httpService.user.subscriptionLastDateTime;

        if (date) {
          let expireDate = undefined;

          if (
            this.httpService.user.subscriptionType ===
            SubscriptionTypeEnum.Monthly
          ) {
            expireDate = dayjs(date).add(1, "month").endOf("day");
          } else if (
            this.httpService.user.subscriptionType ===
            SubscriptionTypeEnum.Yearly
          ) {
            expireDate = dayjs(date).add(1, "year").endOf("day");
          }

          if (expireDate && expireDate > dayjs()) {
            ok = true;
          }
        }
      } else {
        const patterns = await this.httpService.getPatterns(0, "");
        const patternCount = patterns.length;

        if ((patternCount < 3 || !isSaveAs) && !isPrint) {
          ok = true;
        } else {
          // PostHog: Track free tier limit reached
          this.posthog.captureEvent("free_tier_limit_reached", {
            trigger_action: isPrint
              ? "print_pattern"
              : isSaveAs
                ? "save_as_pattern"
                : "save_pattern",
            pattern_count: patternCount,
            free_limit: 3,
            user_tenure_days: this.getUserTenureDays(),
            current_pattern_complexity: this.getPatternComplexity(),
          });
        }
      }

      return ok;
    } else {
      return true;
    }
  }

  private getUserTenureDays(): number {
    // Since UserModel doesn't have createdOn, we'll estimate based on subscription date
    // or return 0 if no data available
    if (this.httpService.user?.subscriptionLastDateTime) {
      return dayjs().diff(
        dayjs(this.httpService.user.subscriptionLastDateTime),
        "day",
      );
    }
    return 0;
  }

  private getPatternComplexity(): number {
    if (!this.bcanvas) return 0;
    const totalCells = this.bcanvas.getRows() * this.bcanvas.getCols();
    const data = this.bcanvas.getData();

    // Count unique colors by checking the pattern data
    const uniqueColors = new Set<string>();
    if (data && data.grid) {
      for (const row of data.grid) {
        for (const bead of row) {
          if (bead && bead.color) {
            uniqueColors.add(bead.color);
          }
        }
      }
    }

    const colorCount = uniqueColors.size;
    // Simple complexity score: (unique colors / 10) * 0.5 + (totalCells / 10000) * 0.5
    return Math.min((colorCount / 10) * 0.5 + (totalCells / 10000) * 0.5, 1);
  }

  private getUniqueColorCount(patternData: any): number {
    if (!patternData || !patternData.grid) {
      return 0;
    }

    const colors = new Set<string>();
    patternData.grid.forEach((row: any[]) => {
      row?.forEach((cell) => {
        if (cell && cell.color) {
          colors.add(cell.color);
        }
      });
    });

    return colors.size;
  }

  public async showSubscription() {
    if (this.httpService.user) {
      if (
        this.httpService.user.stripeCustomerId &&
        this.httpService.user.subscriptionType !== SubscriptionTypeEnum.None
      ) {
        // PostHog: Track customer portal access
        this.posthog.captureEvent("customer_portal_opened", {
          subscription_type: this.httpService.user.subscriptionType,
        });

        await this.showCustomerPortal();
      } else {
        // PostHog: Track subscription modal view
        this.posthog.captureEvent("pricing_page_viewed", {
          user_type: this.httpService.loggedIn ? "logged_in" : "anonymous",
          source: "subscription_modal",
        });

        this.dialog.open(SubscriptionsModal, {
          hasBackdrop: false,
          panelClass: "subscriptions-modal-dialog",
          data: {
            homeComponent: this,
            subscriptionType: this.httpService.user.subscriptionType,
          },
        });
      }
    }
  }

  public async showCustomerPortal() {
    const data = await this.httpService.createCustomerPortalSession();
    // console.log('showCustomerPortal', data);
    window.open(data.redirectUrl, "_blank");
  }

  public async showChangePlanDone() {
    const dialogRef = this.dialog.open(ChangePlanDoneModal, {
      hasBackdrop: false,
      data: {
        homeComponent: this,
      },
    });
  }

  public async savePattern(pattern: PatternModel, isTemporary: boolean) {
    if (!isTemporary) this.pleaseWait = true;

    let data = this.bcanvas.getData();

    try {
      const savedPattern = await this.httpService.savePattern(
        pattern,
        data,
        isTemporary,
      );

      if (!isTemporary) {
        this.pleaseWait = false;

        // Update currentPattern with the saved pattern information from the backend
        if (!this.currentPattern) {
          this.currentPattern = <PatternModel>{};
        }
        this.currentPattern.name = savedPattern.name;
        this.currentPattern.patternId = savedPattern.patternId;
        this.currentPattern.description = savedPattern.description;
        this.currentPattern.categoryId = savedPattern.categoryId;
        this.currentPattern.createdOn = savedPattern.createdOn;
        this.currentPattern.lastUpdateOn = savedPattern.lastUpdateOn;

        // PostHog: Track pattern save success
        this.posthog.captureEvent("pattern_saved", {
          pattern_name: pattern.name,
          width: data.beadWidth,
          height: data.beadHeight,
          total_beads: data.beadWidth * data.beadHeight,
          unique_colors: this.getUniqueColorCount(data),
          stitch_type: data.stitchId,
          is_new: !pattern.patternId || pattern.patternId === 0,
        });

        // Capture whether this is first activation before updating the flag
        const isFirstActivation = !this.hasCompletedFirstSave;

        // PostHog: Track user_activated_first_time (only fires once per user)
        if (isFirstActivation) {
          this.hasCompletedFirstSave = true;

          // Get onboarding context
          const onboardingPath = this.onboardingService.getOnboardingPath();
          const tutorialCompleted = this.isOnboardingTutorialCompleted();
          const onboardingChoiceMadeAt = this.posthog.getSuperProperty(
            "onboarding_choice_made_at",
          );

          // Calculate time from onboarding choice to activation
          let timeFromOnboardingToActivation: number | null = null;
          if (onboardingChoiceMadeAt) {
            const choiceTime = new Date(onboardingChoiceMadeAt).getTime();
            timeFromOnboardingToActivation = Math.round(
              (Date.now() - choiceTime) / 1000,
            );
          }

          this.posthog.captureEvent("user_activated_first_time", {
            pattern_name: pattern.name,
            width: data.beadWidth,
            height: data.beadHeight,
            total_beads: data.beadWidth * data.beadHeight,
            time_to_activation_seconds: Math.round(
              (Date.now() - this.sessionMetrics.sessionStart) / 1000,
            ),
            beads_placed: this.sessionMetrics.beadsPlaced,
            tools_used: this.sessionMetrics.toolsUsed.size,
            // Onboarding context
            onboarding_path: onboardingPath || "none",
            onboarding_tutorial_completed: tutorialCompleted,
            time_from_onboarding_to_activation_seconds:
              timeFromOnboardingToActivation,
          });

          // Set PostHog super property to remember this user has activated
          this.posthog.setSuperProperty("has_activated", true);
          this.posthog.setSuperProperty(
            "activated_at",
            new Date().toISOString(),
          );

          console.log("[Activation] User activated for the first time!", {
            onboarding_path: onboardingPath,
            tutorial_completed: tutorialCompleted,
          });
        }

        // Complete final checklist step (activation!)
        if (this.checklistComponent) {
          this.checklistComponent.completeStep("save");
        }

        // Track interactive tutorial completion if this is first save after choosing upload path
        const onboardingPath = this.onboardingService.getOnboardingPath();
        const hasCompletedTutorial = this.isOnboardingTutorialCompleted();
        if (
          onboardingPath === "upload" &&
          !hasCompletedTutorial &&
          isFirstActivation
        ) {
          this.posthog.captureEvent("onboarding_tutorial_completed", {
            tutorial_type: "interactive",
            timestamp: new Date().toISOString(),
          });
          this.markOnboardingTutorialCompleted("interactive");
        }

        // Reset save reminder tracking after successful save
        console.log("[SaveReminder] Pattern saved - resetting tracking:", {
          previousBeadCount: this.beadPlacementCount,
          hadShownHint: this.hasShownSaveHint,
        });
        this.beadPlacementCount = 0;
        this.lastSaveReminderTime = Date.now();
        this.showSaveReminder = false;

        // Hide mobile save button after successful save
        this.showMobileSaveButton = false;
        this.showMobileSavePulse = false;

        // Check if we should show the early warning banner (2/3 patterns used)
        await this.checkAndShowEarlyWarning();

        // Update toolbar usage indicator
        if (this.appToolbar) {
          await this.appToolbar.updateUsageTracking();
        }
      }
    } catch (error) {
      if (!isTemporary) {
        this.pleaseWait = false;

        // PostHog: Track pattern save failure
        this.posthog.captureEvent("pattern_save_failed", {
          error_status: error?.errorDetails?.status || 0,
          error_message: error?.errorDetails?.message || "Unknown error",
          error_type:
            error?.errorDetails?.status === 0
              ? "network"
              : error?.errorDetails?.status === 408
                ? "timeout"
                : "server",
          pattern_name: error?.patternInfo?.name || pattern.name,
          pattern_id: error?.patternInfo?.patternId || pattern.patternId,
          pattern_size_bytes: error?.patternInfo?.patternSizeBytes || 0,
          pattern_size_kb: error?.patternInfo?.patternSizeKB || 0,
          grid_rows: error?.patternInfo?.gridSize?.rows || 0,
          grid_cols: error?.patternInfo?.gridSize?.cols || 0,
          total_cells:
            (error?.patternInfo?.gridSize?.rows || 0) *
            (error?.patternInfo?.gridSize?.cols || 0),
          is_temporary: error?.patternInfo?.isTemporary || isTemporary,
          has_image: !!this.currentPattern?.imageFilename,
        });

        // Show user-friendly error message
        let errorMessage = "Failed to save pattern. ";
        if (error?.errorDetails?.status === 0) {
          errorMessage +=
            "Please check your internet connection and try again.";
        } else if (error?.errorDetails?.status === 408) {
          errorMessage +=
            "The request timed out. Your pattern may be too large. Try reducing the size.";
        } else {
          errorMessage +=
            error?.errorDetails?.message || "An unknown error occurred.";
        }

        Utility.showNotification(
          this._snackBar,
          errorMessage,
          NotificationTypeEnum.Error,
        );
      }

      // Re-throw for any additional error handling
      throw error;
    }
  }

  /**
   * Check if user has an active subscription (not bypassed by dev mode)
   * Used for determining preview mode in print preview
   */
  private hasActiveSubscription(): boolean {
    if (this.httpService.user.subscriptionType !== SubscriptionTypeEnum.None) {
      const date = this.httpService.user.subscriptionLastDateTime;
      if (date) {
        let expireDate = undefined;
        if (
          this.httpService.user.subscriptionType ===
          SubscriptionTypeEnum.Monthly
        ) {
          expireDate = dayjs(date).add(1, "month").endOf("day");
        } else if (
          this.httpService.user.subscriptionType === SubscriptionTypeEnum.Yearly
        ) {
          expireDate = dayjs(date).add(1, "year").endOf("day");
        }
        if (expireDate && expireDate > dayjs()) {
          return true;
        }
      }
    }
    return false;
  }

  public async showPrintPattern() {
    // Check actual subscription status (not bypassed by dev mode)
    const hasSubscription = this.hasActiveSubscription();
    let data = this.bcanvas.getData();
    const isPreviewMode = !hasSubscription;

    // Track teaser shown and free tier limit for free users
    if (isPreviewMode) {
      this.posthog.captureEvent("print_preview_teaser_shown", {
        pattern_id: this.currentPattern?.patternId,
        total_grid_rows: data.grid?.length || 0,
      });
      // Also track free_tier_limit_reached for analytics continuity
      const patterns = await this.httpService.getPatterns(0, "");
      this.posthog.captureEvent("free_tier_limit_reached", {
        trigger_action: "print_pattern",
        pattern_count: patterns.length,
        free_limit: 3,
        user_tenure_days: this.getUserTenureDays(),
        current_pattern_complexity: this.getPatternComplexity(),
      });
    }

    const dialogRef = this.dialog.open(PrintPreviewModal, {
      maxWidth: "90%",
      width:
        data.stitchId === StitchTypeEnum.Brick &&
        data.beadWidth > data.beadHeight
          ? "1030px"
          : "790px",
      height: this.windowHeight - 20 + "px",
      data: {
        patternName: this.currentPattern ? this.currentPattern.name : "Pattern",
        data: data,
        printContentWidth:
          data.stitchId === StitchTypeEnum.Brick &&
          data.beadWidth > data.beadHeight
            ? 930
            : 690,
        printContentHeight: this.windowHeight - 180,
        isPreviewMode: isPreviewMode,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // If user clicked subscribe button in preview mode, show subscription modal
      if (result && result.action === "subscribe") {
        this.posthog.captureEvent("print_preview_subscribe_clicked", {
          pattern_id: this.currentPattern?.patternId,
        });
        this.showSubscription();
      }
    });
  }

  public async showPatternReader() {
    const hasSubscription = this.hasActiveSubscription();
    const isPreviewMode = !hasSubscription;
    const data = this.bcanvas.getData();

    const dialogRef = this.dialog.open(PatternReaderModal, {
      width: "95%",
      maxWidth: "1200px",
      height: this.windowHeight - 20 + "px",
      panelClass: "pattern-reader-dialog",
      autoFocus: false,
      restoreFocus: false,
      data: {
        patternId: this.currentPattern ? this.currentPattern.patternId : 0,
        patternName: this.currentPattern ? this.currentPattern.name : "Pattern",
        data: data,
        isPreviewMode: isPreviewMode,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.action === "subscribe") {
        this.showSubscription();
      }
    });
  }

  public async showPaletterEditor() {
    const dialogRef = this.dialog.open(PaletteEditorModal, {
      minWidth: "720px",
      height: this.windowHeight - 20 + "px",
      data: {
        paletteListHeight: this.windowHeight - 345,
      },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  public async showLogin() {
    this.googleAuthService.signOut();
  }

  public async uploadFiles(files: any) {
    const startTime = Date.now();

    try {
      this.pleaseWait = true;

      let fileSize = 0;
      let fileType = "";
      let fileExt = "";

      if (files && files.length > 0) {
        const file = files[0];
        fileSize = Math.round(file.size / 1024);
        fileType = file.type;
        fileExt = file.name.split(".").pop();
      }

      await this.httpService.uploadImage(files);

      const duration = Date.now() - startTime;

      // PostHog: Track successful image upload with performance data
      this.posthog.captureEvent("image_uploaded", {
        file_size_kb: fileSize,
        original_file_size_mb: Math.round((fileSize / 1024) * 100) / 100,
        file_type: fileType,
        file_name_extension: fileExt,
        upload_duration_ms: duration,
        is_slow: duration > 5000, // Flag if > 5 seconds
        device_memory_mb: (performance as any).memory?.jsHeapSizeLimit
          ? Math.round(
              (performance as any).memory.jsHeapSizeLimit / (1024 * 1024),
            )
          : null,
        is_mobile: /Android|iPhone|iPad/i.test(navigator.userAgent),
        user_agent: navigator.userAgent,
      });

      // Track slow uploads separately for visibility
      if (duration > 5000) {
        this.posthog.captureEvent("slow_upload_detected", {
          file_size_kb: fileSize,
          upload_duration_ms: duration,
          file_type: fileType,
        });
      }

      // Mark upload hint as completed
      localStorage.setItem("hint_upload_completed", "true");

      // Show position guide modal
      // Note: The checklist step will be completed AFTER the modal closes
      // This prevents auto-opening the next menu while the modal is still visible
      this.showPositionGuideModal();
    } catch (error) {
      const duration = Date.now() - startTime;

      // Determine error type and user-friendly message
      let errorType = "unknown";
      let errorMessage = "An error occurred during upload!";
      let httpStatus = 0;

      if (error) {
        // Capture HTTP status if available
        httpStatus = error?.status || 0;

        // Categorize error type
        if (error.message === "FILE_TOO_LARGE") {
          errorType = "file_too_large";
          errorMessage =
            "Image file is too large. Please use an image under 15MB.";
        } else if (error.message === "MEMORY_ERROR") {
          errorType = "memory_error";
          errorMessage =
            "Image is too large for your device. Please try a smaller image or use a desktop browser.";
        } else if (
          httpStatus === 413 ||
          (error.message && error.message.includes("too large"))
        ) {
          errorType = "file_too_large";
          errorMessage = "Image file is too large. Maximum size is 15MB.";
        } else if (
          httpStatus === 415 ||
          (error.message && error.message.includes("format"))
        ) {
          errorType = "unsupported_format";
          errorMessage =
            "Unsupported image format. Please use JPEG, PNG, or GIF.";
        } else if (
          httpStatus === 0 ||
          (error.message && error.message.includes("network"))
        ) {
          errorType = "network_error";
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else if (error.message) {
          errorMessage = error.message;
          errorType = "server_error";
        }
      }

      // PostHog: Track upload error with comprehensive details
      const fileSizeKB =
        files && files.length > 0 ? Math.round(files[0].size / 1024) : 0;
      this.posthog.captureEvent("image_upload_failed", {
        error_type: errorType,
        error_message: errorMessage,
        error_status: httpStatus,
        duration_before_error_ms: duration,
        file_size_kb: fileSizeKB,
        file_size_mb: Math.round((fileSizeKB / 1024) * 100) / 100,
        file_type: files && files.length > 0 ? files[0].type : "",
        file_name_extension:
          files && files.length > 0 ? files[0].name.split(".").pop() : "",
        is_memory_error: errorType === "memory_error",
        device_heap_limit_mb: (performance as any).memory?.jsHeapSizeLimit
          ? Math.round(
              (performance as any).memory.jsHeapSizeLimit / (1024 * 1024),
            )
          : null,
        user_agent: navigator.userAgent,
      });

      Utility.showNotification(
        this._snackBar,
        errorMessage,
        NotificationTypeEnum.Error,
      );
    } finally {
      this.pleaseWait = false;
    }
  }

  private checkImageAndPatternOverlap(): boolean {
    var model = this.bcanvas.getModel();

    if (model && model.getPatternImage()) {
      const patternRect = new Rect(
        Math.round(model.getPosition().x),
        Math.round(model.getPosition().y),
        model.getBeadSize().width * model.getCols(),
        model.getBeadSize().height * model.getRows(),
      );

      const imageRect = new Rect(
        Math.round(model.getPatternImage().pos.x),
        Math.round(model.getPatternImage().pos.y),
        Math.round(model.getPatternImage().size.width),
        Math.round(model.getPatternImage().size.height),
      );

      return patternRect.intersectRect(imageRect);
    } else {
      return false;
    }
  }

  public async showTransferImage() {
    const dialogRef = this.dialog.open(TransferImageModal, {
      width: "500px",
      maxWidth: "95vw",
      hasBackdrop: false,
      disableClose: false,
      panelClass: "transfer-image-dialog",
      data: {},
    });

    dialogRef.componentInstance.onApplyClicked.subscribe(async (result) => {
      // console.log('The dialog was closed');
      // console.log(result);

      let transferImageResult = <TransferImageModalResult>result;

      // console.log(transferImageResult);

      if (transferImageResult) {
        // Check if selected palette is compatible with existing beads in the pattern
        const patternBuiltInPaletteId =
          this.bcanvas.getPatternBuiltInPaletteId();
        const selectedPaletteId = transferImageResult.selectedPalette.paletteId;
        const selectedBuiltInPaletteId =
          getBuiltInPaletteId(
            {
              paletteId: selectedPaletteId,
              originalPaletteId: null,
            } as any,
            this.paletteRegistry,
          ) || selectedPaletteId;

        // Check if the selected palette is a known built-in palette
        const isSelectedBuiltIn = this.paletteRegistry.isBuiltInPaletteId(
          selectedBuiltInPaletteId,
        );

        if (
          patternBuiltInPaletteId &&
          isSelectedBuiltIn &&
          patternBuiltInPaletteId !== selectedBuiltInPaletteId
        ) {
          const paletteName = getBuiltInPaletteName(
            patternBuiltInPaletteId,
            this.paletteRegistry,
          );
          const errorMessage = Utility.formatString(
            this.httpService.translation.PaletteMismatchError,
            paletteName,
          );
          Utility.showNotification(
            this._snackBar,
            errorMessage,
            NotificationTypeEnum.Warning,
          );
          return;
        }

        if (this.checkImageAndPatternOverlap()) {
          this.pleaseWait = true;

          try {
            var model = this.bcanvas.getModel();

            await this.httpService.transferImage(
              transferImageResult.selectedPalette.paletteId,
              transferImageResult.nColors,
              transferImageResult.selectedTransferPreEffect.preEffect,
              transferImageResult.selectedTransferMethod.method,
              transferImageResult.selectedTransferDither.dither,
              model,
            );

            // Hide transfer hints and show save hint
            this.onboardingService.showImageTransferredHint();

            // Show save hint after a short delay
            setTimeout(() => {
              this.onboardingService.showSaveReminderHint();
            }, 200);

            // Complete checklist step: convert_pattern (for upload path)
            if (this.checklistComponent) {
              this.checklistComponent.completeStep("convert_pattern");
            }

            // Show mobile save button after transfer
            this.updateMobileSaveButtonVisibility();
          } catch (error) {
            Utility.showNotification(
              this._snackBar,
              "An error occurred during the transfer!",
              NotificationTypeEnum.Error,
            );
          } finally {
            this.pleaseWait = false;
          }
        } else {
          Utility.showNotification(
            this._snackBar,
            "Image and pattern must overlap!",
            NotificationTypeEnum.Error,
          );
        }
      }
    });
  }

  public async showTransformPattern() {
    const dialogRef = this.dialog.open(TransformPatternModal, {
      hasBackdrop: false,
      data: {
        bcanvas: this.bcanvas,
        homeComponent: this,
      },
    });
  }

  async refreshCanvas(
    patternModel: PatternModel,
    patternImage: HTMLImageElement,
  ) {
    const newData = this.httpService.deserializePatternBead(
      patternModel.patternData,
    );
    if (newData.grid.length === 0) {
      this.newPattern();
      return;
    }

    this.bcanvas.setData(newData);

    if (patternImage) {
      this.bcanvas.setPatternImage(patternImage);
    }

    // console.log(
    //   this.bcanvas.getData().posX,
    //   this.bcanvas.getData().posY,
    //   this.bcanvas.getZoomFactor()
    // );

    //console.log(patternModel.patternData);
    this.patternPaletteList.refreshPaletteDetails();
    this.bcanvas.repaint();
    await this.httpService.updateLastActionId();
    this.appToolbar.refreshRotatedTooltip();

    // Select the palette that matches the pattern's beads
    const patternPaletteId = this.bcanvas.getPatternBuiltInPaletteId();
    if (patternPaletteId && this.paletteList) {
      await this.paletteList.selectPaletteById(patternPaletteId);
    }

    // Skip scroll during initial load - let the welcome modal handler or
    // ngAfterViewInit handle positioning to avoid conflicts
    if (!this.isInitialLoad) {
      this.bcanvas.scrollCanvasContainerToPatternPosition();
    }
  }

  onActionFired(action: Action) {
    // Track pattern changes (action parameter used by caller)
    this.patternPaletteList.refreshPaletteDetails();

    // Track bead placements for save reminder - only count actual bead placement actions
    // SetBead = single bead placed/removed
    // Fill = fill tool used
    // Composite = multiple beads (e.g., from fill or transfer)
    // NOTE: Using action.actionType instead of action.constructor.name because
    // constructor.name gets minified in production builds (e.g., 'a' instead of 'SetBeadAction')
    const actionType = action.actionType;

    if (actionType === "SetBead" || actionType === "Fill") {
      this.trackBeadPlacement();

      // PostHog: Track pattern creation on FIRST meaningful edit
      if (!this.hasCreatedPattern) {
        this.hasCreatedPattern = true;

        // Fire pattern_created_first_time (once per user lifetime)
        this.posthog.captureEvent("pattern_created_first_time", {
          first_interaction_type:
            actionType === "SetBead" ? "place_bead" : "fill",
          time_to_first_creation_seconds: Math.round(
            (Date.now() - this.sessionMetrics.sessionStart) / 1000,
          ),
        });

        // Set PostHog super property to remember this user has created a pattern
        this.posthog.setSuperProperty("has_created_first_pattern", true);

        console.log("[Activation] User created their first pattern!");
      }

      // Also fire pattern_created for per-session tracking (existing event)
      this.posthog.captureEvent("pattern_created", {
        interaction_type: actionType === "SetBead" ? "place_bead" : "fill",
        session_duration_seconds: Math.round(
          (Date.now() - this.sessionMetrics.sessionStart) / 1000,
        ),
      });

      // No checklist steps to complete for manual bead placement
      // (Tutorial is focused on image-to-pattern workflow)
    }
  }

  private trackBeadPlacement() {
    this.beadPlacementCount++;

    // Update mobile save button visibility
    this.updateMobileSaveButtonVisibility();

    console.log("[SaveReminder] Bead placement tracked:", {
      count: this.beadPlacementCount,
      hasShownHint: this.hasShownSaveHint,
      canSave: this.httpService.canSavePattern(),
      timeSinceLastReminder: Date.now() - this.lastSaveReminderTime,
    });

    // Show save hint after 12 bead placements (first time only) using new onboarding service
    if (!this.hasShownSaveHint && this.beadPlacementCount >= 12) {
      console.log(
        "[SaveReminder] Showing FIRST-TIME hint at",
        this.beadPlacementCount,
        "beads",
      );
      this.hasShownSaveHint = true;
      this.lastSaveReminderTime = Date.now(); // Reset timer after showing first hint

      // Show directive tooltip for saving
      this.onboardingService.showSaveReminderHint();

      return; // Don't check the 2-minute condition yet
    }

    // Show reminder every 2 minutes of activity (only after first hint has been shown)
    if (this.hasShownSaveHint) {
      const now = Date.now();
      const timeSinceLastReminder = now - this.lastSaveReminderTime;
      const twoMinutesInMs = 120000;

      console.log("[SaveReminder] Checking 2-minute condition:", {
        timeSinceLastReminder: Math.floor(timeSinceLastReminder / 1000) + "s",
        twoMinutesInMs: twoMinutesInMs / 1000 + "s",
        canSave: this.httpService.canSavePattern(),
        shouldShow:
          timeSinceLastReminder > twoMinutesInMs &&
          this.httpService.canSavePattern(),
      });

      if (
        this.httpService.canSavePattern() &&
        timeSinceLastReminder > twoMinutesInMs
      ) {
        console.log(
          "[SaveReminder] Showing RECURRING reminder (2 minutes passed)",
        );
        this.lastSaveReminderTime = now;
        this.showSaveReminder = true;

        setTimeout(() => {
          console.log(
            "[SaveReminder] Auto-hiding recurring reminder after 8 seconds",
          );
          this.showSaveReminder = false;
        }, 8000);
      }
    }
  }

  public dismissSaveReminder() {
    console.log("[SaveReminder] User manually dismissed the reminder");
    this.showSaveReminder = false;
  }

  public dismissEarlyWarningBanner() {
    this.showEarlyWarningBanner = false;
    this.earlyWarningDismissed = true;
    localStorage.setItem("early_warning_dismissed", "true");

    // PostHog: Track dismissal
    this.posthog.captureEvent("early_warning_dismissed", {
      pattern_count: 2,
      dismissal_method: "close_button",
    });
  }

  public onEarlyWarningUpgradeClick() {
    this.showEarlyWarningBanner = false;

    // PostHog: Track upgrade click from early warning
    this.posthog.captureEvent("early_warning_upgrade_clicked", {
      pattern_count: 2,
      banner_position: "top_banner",
    });

    this.showSubscription();
  }

  private async checkAndShowEarlyWarning() {
    // Only show for free users who haven't dismissed it
    if (this.httpService.user.subscriptionType !== SubscriptionTypeEnum.None) {
      return;
    }

    if (
      this.earlyWarningDismissed ||
      localStorage.getItem("early_warning_dismissed") === "true"
    ) {
      return;
    }

    try {
      const patterns = await this.httpService.getPatterns(0, "");
      const patternCount = patterns.length;

      if (patternCount === 2) {
        this.showEarlyWarningBanner = true;

        // PostHog: Track early warning shown
        this.posthog.captureEvent("early_warning_shown", {
          pattern_count: patternCount,
          remaining_patterns: 1,
          trigger_point: "after_save",
        });
      }
    } catch (error) {
      console.error("Error checking pattern count for early warning:", error);
    }
  }

  onStitchSelected(stitch: StitchModel) {
    this.bcanvas.controller.changeStitch(stitch.stitchId);
    this.bcanvas.repaint();
  }

  selectedPaletteDetailChanged(paletteDetail: PaletteDetailModel) {
    this.selectedPaletteDetail = paletteDetail;
    this.currentBeadColor = paletteDetail ? paletteDetail.color : null;
    this.bcanvas.setCurrentBead(this.selectedPaletteDetail);

    // console.log("selectedPaletteDetailChanged");
    // console.log(this.selectedPaletteDetail);
    if (Utility.hasTouchScreen()) {
      this.rightSidebarOpen = false;
      this.beadCanvasComponent.resizeCanvasContainer();
    }
  }

  onPaletteDetailsLoaded() {
    if (
      !this.patternPaletteList.selectedPaletteDetail &&
      !this.paletteList.selectedPaletteDetail &&
      this.paletteList.paletteDetails.length > 0
    ) {
      this.paletteList.selectedPaletteDetail =
        this.paletteList.paletteDetails[0];
      this.paletteList.selectedPaletteDetailChanged.emit(
        this.paletteList.selectedPaletteDetail,
      );
    }
  }

  getColumnsSize() {
    return Utility.convertBeadsToSize(
      this.bcanvas.getStitchId(),
      this.bcanvas.getCols(),
      this.getCurrentBeadType(),
      BeadMeasureEnum.Width,
      this.httpService.user
        ? this.httpService.user.unitOfMeasure
        : UnitOfMeasureEnum.Inch,
    );
  }

  getRowsSize() {
    return Utility.convertBeadsToSize(
      this.bcanvas.getStitchId(),
      this.bcanvas.getRows(),
      this.getCurrentBeadType(),
      BeadMeasureEnum.Height,
      this.httpService.user
        ? this.httpService.user.unitOfMeasure
        : UnitOfMeasureEnum.Inch,
    );
  }

  /**
   * Determine the current bead type based on:
   * 1. If pattern has beads: use the originalPaletteId from pattern beads
   * 2. If pattern is empty: use the selected palette in Palettes tab
   */
  private getCurrentBeadType(): BeadTypeEnum {
    // First, check if pattern has beads
    if (this.patternPaletteList?.paletteDetails?.length > 0) {
      // Use the first bead's original palette ID to determine bead type
      const firstBead = this.patternPaletteList.paletteDetails[0];
      const paletteId = firstBead.originalPaletteId || firstBead.paletteId;
      return getBeadTypeForPalette(paletteId, this.paletteRegistry);
    }

    // If pattern is empty, use the selected palette in Palettes tab
    if (this.paletteList?.selectedPalette) {
      const selectedPalette = this.paletteList.selectedPalette;
      // For custom palettes, we need to look at the originalPaletteId of beads
      // For built-in palettes, use the paletteId directly
      if (selectedPalette.builtIn) {
        return getBeadTypeForPalette(
          selectedPalette.paletteId,
          this.paletteRegistry,
        );
      } else {
        // For custom palettes, check if there's a selected bead with originalPaletteId
        if (this.paletteList.selectedPaletteDetail?.originalPaletteId) {
          return getBeadTypeForPalette(
            this.paletteList.selectedPaletteDetail.originalPaletteId,
            this.paletteRegistry,
          );
        }
        // If no selected bead, try to get from first bead in the palette details
        if (this.paletteList.paletteDetails?.length > 0) {
          const firstBead = this.paletteList.paletteDetails[0];
          if (firstBead.originalPaletteId) {
            return getBeadTypeForPalette(
              firstBead.originalPaletteId,
              this.paletteRegistry,
            );
          }
        }
      }
    }

    // Default to Miyuki Delica 11
    return BeadTypeEnum.MiyukiDelica11;
  }

  onColsChange(ev: any) {
    const newCol = ev.target.value as number;

    if (newCol > 0) {
      this.bcanvas.setCols(ev.target.value as number);
    }
  }

  onRowsChange(ev: any) {
    const newRow = ev.target.value as number;

    if (newRow > 0) {
      this.bcanvas.setRows(ev.target.value as number);
    }
  }

  // Help panel methods
  toggleHelpPanel(): void {
    this.helpPanelOpen = !this.helpPanelOpen;
  }

  closeHelpPanel(): void {
    this.helpPanelOpen = false;
  }

  get isOnboardingVisible(): boolean {
    return this.checklistComponent?.isVisible ?? false;
  }

  get hasOpenModals(): boolean {
    return this.dialogOpenCount > 0;
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    // Handle Escape key
    if (event.key === "Escape" && this.helpPanelOpen) {
      event.preventDefault();
      this.closeHelpPanel();
      return;
    }

    // Check if user is typing in an input field - don't intercept keyboard shortcuts
    const target = event.target as HTMLElement;
    const isInputField =
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable;

    // Don't intercept shortcuts when modals are open
    if (this.hasOpenModals) {
      return;
    }

    // Only block shortcuts when actually typing in text input fields
    // Don't block for other focusable elements like radio buttons, selects, etc.
    if (
      isInputField &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
    ) {
      const inputElement = target as HTMLInputElement | HTMLTextAreaElement;
      // Only block if it's a text-based input where user would type
      const textInputTypes = [
        "text",
        "email",
        "password",
        "search",
        "tel",
        "url",
        "number",
      ];
      if (
        target.tagName === "TEXTAREA" ||
        textInputTypes.includes(inputElement.type)
      ) {
        return;
      }
    }

    // Tool shortcuts (only when not holding Ctrl/Cmd)
    if (!event.ctrlKey && !event.metaKey) {
      switch (event.key.toLowerCase()) {
        case "b":
          // Switch to Brush/Add Bead mode
          this.selectedEditMode = "add-bead";
          this.bcanvas.setEditMode(EditMode.AddBead, false);
          event.preventDefault();
          break;
        case "e":
          // Switch to Eraser/Remove Bead mode
          this.selectedEditMode = "remove-bead";
          this.bcanvas.setEditMode(EditMode.RemoveBead, false);
          event.preventDefault();
          break;
        case "f":
          // Switch to Fill mode
          this.selectedEditMode = "fill";
          this.bcanvas.setEditMode(EditMode.Fill, false);
          event.preventDefault();
          break;
        case "r":
          // Switch to Remove Fill mode
          this.selectedEditMode = "remove-fill";
          this.bcanvas.setEditMode(EditMode.RemoveFill, false);
          event.preventDefault();
          break;
        case "i":
          // Switch to Pick Bead/Color Picker mode
          this.selectedEditMode = "pick-bead";
          this.bcanvas.setEditMode(EditMode.PickBead, false);
          event.preventDefault();
          break;
        case " ":
          // Switch to Pan mode (spacebar)
          this.selectedEditMode = "pan";
          this.bcanvas.setEditMode(EditMode.Pan, false);
          event.preventDefault();
          break;
      }
    }
  }

  launchHelpTutorial(): void {
    this.closeHelpPanel();
    this.openVideoTutorial("help-panel");
  }

  copySupportEmailToClipboard(): void {
    const email = "support@beadpatternmaker.com";

    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern clipboard API
      navigator.clipboard
        .writeText(email)
        .then(() => {
          this.emailCopied = true;
          setTimeout(() => {
            this.emailCopied = false;
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy email:", err);
          this.fallbackCopyToClipboard(email);
        });
    } else {
      // Fallback for older browsers
      this.fallbackCopyToClipboard(email);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      this.emailCopied = true;
      setTimeout(() => {
        this.emailCopied = false;
      }, 2000);
    } catch (err) {
      console.error("Fallback copy failed:", err);
    }

    document.body.removeChild(textArea);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.paletteMismatchSubscription) {
      this.paletteMismatchSubscription.unsubscribe();
      this.paletteMismatchSubscription = null;
    }
  }
}
